import '../../../shared/StyledInput.css';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import FieldHelp from '../../../shared/FieldHelp';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { PAYMENT_MESSAGE_FIELDS, formatBsb } from '../../../shared/paymentOptions';

const PAGE_KEY = 'receivables-payment-options';
const sectionStyle = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: 16 };
const headerRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 };
const titleStyle = { margin: 0, fontSize: 15, fontWeight: 600 };

function toForm(options) {
  return options.map((o) => ({
    option_key: o.option_key,
    is_on: !!o.is_on,
    account_id: o.account_id ? String(o.account_id) : '',
    message: o.message || '',
    payid_type: o.payid_type || 'email',
    payid: o.payid || '',
    payid_name: o.payid_name || '',
  }));
}

// The message under an option, with the same insert-at-cursor chip as the
// Quote Price Reminder (SalesSettingsPage.js).
function MessageField({ id, label, value, placeholder, onChange }) {
  const ref = useRef(null);
  const insertField = (key) => {
    const el = ref.current;
    const token = `{${key}}`;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    onChange(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };
  return (
    <div style={{ marginTop: 10 }}>
      <label htmlFor={id}>{label}</label><br />
      <input
        id={id}
        ref={ref}
        className="styled-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={255}
        placeholder={placeholder}
        style={{ width: '100%' }}
      />
      <div style={{ fontSize: 12, color: '#888', marginTop: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        Insert a field:
        {PAYMENT_MESSAGE_FIELDS.map((f) => (
          <span key={f.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              title={f.label}
              onClick={() => insertField(f.key)}
              style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 8px', borderRadius: 12, border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer' }}
            >
              {`{${f.key}}`}
            </button>
            {f.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function AccountSelect({ id, label, value, accounts, onChange }) {
  return (
    <>
      <label htmlFor={id}>{label}</label><br />
      <select id={id} className="styled-input" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%' }}>
        <option value="">None</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>{a.account_number} {a.account_name}{a.is_active ? '' : ' (inactive)'}</option>
        ))}
      </select>
    </>
  );
}

// Payment Options (issue #450): the ways a Prepay Customer can pay, each
// switched on or off here and shown on their Quotes and Sales Orders.
// A saved option's own details stay when it's switched off.
function PaymentOptionsPage({ onNavigate }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [form, setForm] = useState([]);
  const [missing, setMissing] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  const applySaved = (options) => {
    setForm(toForm(options));
    setMissing(Object.fromEntries(options.map((o) => [o.option_key, o.missing])));
  };

  useEffect(() => {
    axios.get('/api/payment-options')
      .then((res) => {
        applySaved(res.data.data.options);
        setBankAccounts(res.data.data.bank_accounts || []);
      })
      .catch((err) => showError(err, 'Failed to load Payment Options'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const setOption = (key, changes) => {
    setSuccess('');
    setForm((rows) => rows.map((r) => (r.option_key === key ? { ...r, ...changes } : r)));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      const res = await axios.put('/api/payment-options', { options: form });
      applySaved(res.data.data.options);
      setSuccess('Payment Options saved.');
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setSuccess('');
    if (onNavigate) onNavigate('receivables-dashboard');
  };

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 760, margin: '32px auto' }}>Loading...</div>;
  }

  const bank = form.find((o) => o.option_key === 'bank_transfer');
  const payid = form.find((o) => o.option_key === 'payid');
  const picked = bank && bankAccounts.find((a) => String(a.id) === String(bank.account_id));
  const pickedFilled = picked && picked.bank_account_name && picked.bank_bsb && picked.bank_account_number;

  const missingNote = (option) => (option.is_on && missing[option.option_key]?.length ? (
    <div style={{ fontSize: 13, color: '#b45309', marginTop: 10 }}>
      Nothing prints for this option yet. Still needed: {missing[option.option_key].join(', ')}.
    </div>
  ) : null);

  return (
    <div style={{ width: '100%', maxWidth: 760, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Payment Options</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>The ways a Prepay Customer can pay, shown on their Quotes and Sales Orders.</p>

      <form onSubmit={handleSave}>
        {bank && (
          <div style={sectionStyle}>
            <div style={headerRowStyle}>
              <h4 style={titleStyle}>Bank Transfer</h4>
              <ToggleSwitch checked={bank.is_on} onChange={(v) => setOption('bank_transfer', { is_on: v })} label={bank.is_on ? 'On' : 'Off'} />
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, maxWidth: 420 }}>
                <AccountSelect id="bank_transfer_account" label="Printed Bank Account" value={bank.account_id} accounts={bankAccounts} onChange={(v) => setOption('bank_transfer', { account_id: v })} />
              </div>
              <FieldHelp fieldKey="foundation-company-settings.bank-details" pageKey={PAGE_KEY} label="Bank Transfer">
                The bank Account whose account name, BSB and account number show on the Quotes and Sales Orders of Customers whose Payment Term is marked Prepay, printed or emailed. Nothing shows until all three are filled in on the Account (Accounting › Accounts). A change shows on every document printed or emailed from then on.
              </FieldHelp>
            </div>
            {pickedFilled && (
              <div style={{ fontSize: 13, color: '#5a6b85', marginTop: 6 }}>
                {`${picked.bank_account_name} · BSB ${formatBsb(picked.bank_bsb)} · Acc ${picked.bank_account_number}`}
              </div>
            )}
            <MessageField
              id="bank_transfer_message"
              label="Payment Reference Instruction"
              value={bank.message}
              placeholder="e.g. Use {document_number} as your payment reference."
              onChange={(v) => setOption('bank_transfer', { message: v })}
            />
            {missingNote(bank)}
          </div>
        )}

        {payid && (
          <div style={sectionStyle}>
            <div style={headerRowStyle}>
              <h4 style={titleStyle}>PayID</h4>
              <ToggleSwitch checked={payid.is_on} onChange={(v) => setOption('payid', { is_on: v })} label={payid.is_on ? 'On' : 'Off'} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ width: 180 }}>
                <label htmlFor="payid_type">PayID type</label><br />
                <select id="payid_type" className="styled-input" value={payid.payid_type} onChange={(e) => setOption('payid', { payid_type: e.target.value })} style={{ width: '100%' }}>
                  <option value="email">Email address</option>
                  <option value="phone">Phone number</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label htmlFor="payid">PayID</label><br />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    id="payid"
                    className="styled-input"
                    value={payid.payid}
                    onChange={(e) => setOption('payid', { payid: e.target.value })}
                    maxLength={254}
                    placeholder={payid.payid_type === 'phone' ? 'e.g. 0412 345 678' : 'e.g. accounts@yourbusiness.com.au'}
                    style={{ width: '100%' }}
                  />
                  <FieldHelp fieldKey="receivables-payment-options.payid" pageKey={PAGE_KEY} label="PayID">
                    The email address or mobile number registered with your bank as your PayID. The Customer enters it to pay, and their bank then shows the PayID name so they can check who they are paying. Your BSB and account number stay off the document.
                  </FieldHelp>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label htmlFor="payid_name">PayID name</label><br />
                <input
                  id="payid_name"
                  className="styled-input"
                  value={payid.payid_name}
                  onChange={(e) => setOption('payid', { payid_name: e.target.value })}
                  maxLength={140}
                  placeholder="As registered with your bank"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <AccountSelect id="payid_account" label="Linked Bank Account" value={payid.account_id} accounts={bankAccounts} onChange={(v) => setOption('payid', { account_id: v })} />
              </div>
            </div>
            <MessageField
              id="payid_message"
              label="PayID Message"
              value={payid.message}
              placeholder="e.g. Use {document_number} as the description."
              onChange={(v) => setOption('payid', { message: v })}
            />
            {missingNote(payid)}
          </div>
        )}

        {success && <div style={{ color: '#2e7d32', marginTop: 12 }}>{success}</div>}

        <div style={{ marginTop: 16 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
    </div>
  );
}

export default PaymentOptionsPage;
